import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationPipe } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { takeUntil, finalize, switchMap, map } from 'rxjs/operators';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { DataImportService } from './services/data-import.service';
import { ImportCardComponent } from './components/import-card/import-card.component';
import { ImportSource, ImportState, ImportScope, ImportStatus } from './models';

/**
 * Main component for managing data imports
 * Handles CA and VA import operations, status monitoring, and polling
 */
@Component({
  selector: 'app-data-import',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, ImportCardComponent],
  templateUrl: './data-import.component.html',
  styleUrls: ['./data-import.component.scss'],
})
export class DataImportComponent implements OnInit, OnDestroy {
  // Expose enums to template
  readonly ImportSource = ImportSource;
  readonly ImportStatus = ImportStatus;

  isLoading = true;

  caState: ImportState = this.getInitialState();
  vaState: ImportState = this.getInitialState();

  caSystemStartDate: Date | null = null;
  vaSystemStartDate: Date | null = null;

  startingImportSource: ImportSource | null = null;

  private destroy$ = new Subject<void>();
  private pollingSubscriptions: Map<ImportSource, Subscription> = new Map();

  constructor(
    private readonly dataImportService: DataImportService,
    private readonly confirmationService: ConfirmationService,
    private readonly toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAllPolling();
  }

  onStartImport(source: ImportSource, scope: ImportScope): void {
    this.confirmationService
      .info(
        'Settings::DataImport:Confirmation:Message',
        'Settings::DataImport:Confirmation:Title',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::DataImport:Confirmation:Yes',
          cancelText: 'Settings::DataImport:Confirmation:Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            this.startingImportSource = source;
            return this.dataImportService.startImport({ source, scope }).pipe(
              map(() => true),
              finalize(() => {
                this.startingImportSource = null;
              }),
            );
          }
          return [false];
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: started => {
          if (started) {
            this.toasterService.success(
              'Settings::DataImport:Toast:ImportStarted',
              'Settings::DataImport:Toast:Success',
              null,
            );
            this.startPolling(source);
          }
        }
      });
  }
  onStopImport(source: ImportSource): void {
    this.confirmationService
      .info(
        'Settings::DataImport:Confirmation:StopMessage',
        'Settings::DataImport:Confirmation:StopTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::DataImport:Confirmation:StopYes',
          cancelText: 'Settings::DataImport:Confirmation:Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.dataImportService.stopImport(source).pipe(map(() => true));
          }
          return of(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: stopped => {
          if (stopped) {
            this.toasterService.success(
              'Settings::DataImport:Toast:ImportStopped',
              'Settings::DataImport:Toast:Success'
            );
            this.stopPolling(source);
            this.dataImportService
              .getImportStatus(source)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: status => {
                  const state = this.mapToImportState(status);
                  if (source === ImportSource.CA) {
                    this.caState = state;
                  } else {
                    this.vaState = state;
                  }
                },
              });
          }
        },
      });
  }

  private loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      caStatus: this.dataImportService.getImportStatus(ImportSource.CA),
      vaStatus: this.dataImportService.getImportStatus(ImportSource.VA),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ caStatus, vaStatus }) => {
          this.caState = this.mapToImportState(caStatus);
          this.vaState = this.mapToImportState(vaStatus);

          if (
            this.caState.status === ImportStatus.Pending ||
            this.caState.status === ImportStatus.Importing
          ) {
            this.startPolling(ImportSource.CA);
          }
          if (
            this.vaState.status === ImportStatus.Pending ||
            this.vaState.status === ImportStatus.Importing
          ) {
            this.startPolling(ImportSource.VA);
          }
        },
        error: error => {
          console.error('Failed to load initial data:', error);
        },
      });
  }

  private startPolling(source: ImportSource): void {
    if (this.pollingSubscriptions.has(source)) {
      return;
    }

    const subscription = this.dataImportService
      .pollImportStatus(source, 10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          const state = this.mapToImportState(status);
          if (source === ImportSource.CA) {
            this.caState = state;
          } else {
            this.vaState = state;
          }

          if (state.status === ImportStatus.Completed) {
            this.stopPolling(source);
          }
        },
        error: error => {
          console.error(`Polling error for ${ImportSource[source]}:`, error);
          this.stopPolling(source);
        },
      });

    this.pollingSubscriptions.set(source, subscription);
  }

  private stopPolling(source: ImportSource): void {
    const subscription = this.pollingSubscriptions.get(source);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(source);
    }
  }

  private stopAllPolling(): void {
    this.pollingSubscriptions.forEach(sub => sub.unsubscribe());
    this.pollingSubscriptions.clear();
  }

  private getInitialState(): ImportState {
    return {
      status: ImportStatus.NotStarted,
      progress: 0,
      timeRemaining: null,
      errorCount: 0,
      totalRecords: 0,
      completedRecords: 0,
      pendingRecords: 0,
      systemStartDate: null,
      lastImport: null,
      progressDetails: [],
    };
  }

  private mapToImportState(status: any): ImportState {
    const mapStatus = (raw: any): ImportStatus => {
      if (raw === ImportStatus.NotStarted || raw === 0) return ImportStatus.NotStarted;
      if (raw === ImportStatus.Pending || raw === 1) return ImportStatus.Pending;
      if (raw === ImportStatus.Importing || raw === 2) return ImportStatus.Importing;
      if (raw === ImportStatus.Completed || raw === 3) return ImportStatus.Completed;
      return ImportStatus.NotStarted;
    };

    const mappedStatus = mapStatus(status.status);
    const isActive = mappedStatus === ImportStatus.Pending || mappedStatus === ImportStatus.Importing;

    return {
      status: mappedStatus,
      progress: mappedStatus === ImportStatus.NotStarted ? 0 : (status.progress ?? 0),
      timeRemaining: isActive ? (status.timeRemaining ?? null) : null,
      errorCount: status.errorRecords ?? status.errorCount ?? 0,
      totalRecords: status.totalRecords ?? 0,
      completedRecords: status.completedRecords ?? 0,
      pendingRecords: status.pendingRecords ?? 0,
      systemStartDate: status.systemStartDate ? new Date(status.systemStartDate) : null,
      lastImport: status.lastImport ? new Date(status.lastImport) : null,
      progressDetails: status.progressDetails ?? [],
    };
  }
}
